/**
 * Shared vocabulary for the signal-flow diagrams: design-system colours, the
 * four states a lane can be in, and the two animations.
 *
 * Kept apart from the components so the Test lab, Simulive and Live panels
 * colour identically without importing each other.
 */

import { keyframes } from '@emotion/react';
import type { Theme } from '@mui/material';

/**
 * Design tokens, by the name they carry in `styles/style.css`.
 *
 * `muiTheme` spreads the parsed CSS variables straight onto `palette`, so these
 * are on the theme at runtime but not on MUI's `Palette` type — hence the cast.
 * The fallback repeats what style.css declares and only applies if that parse
 * changes shape; reading tokens this way is the existing convention here (see
 * PlayerInfoHeader and ShiftCard).
 */
const TOKEN_FALLBACKS = {
  wash200: '#DBDBE1',
  wash500: '#676774',
  wash600: '#4A4A55',
  wash650: '#40404A',
  wash700: '#37373F',
  wash850: '#1F1F25',
  wash950: '#101013',
  green500: '#12b76a',
  yellow500: '#fdb022',
  red500: '#f04438',
} as const;

export type FlowToken = keyof typeof TOKEN_FALLBACKS;

export const flowToken = (theme: Theme, name: FlowToken): string =>
  (theme.palette as unknown as Record<string, string | undefined>)[name] ??
  TOKEN_FALLBACKS[name];

/**
 * What a lane is doing, reduced to the four things the diagram colours by.
 *
 * Each panel maps its own vocabulary onto these — TM's `running`/`error`, a
 * replay's `listening`/`ingesting`, EC2's `pending` — so the palette lives in
 * one place while the state machines stay where they belong.
 */
export type FlowTone = 'idle' | 'pending' | 'active' | 'error';

const TONE_TEXT: Record<FlowTone, FlowToken> = {
  idle: 'wash500',
  pending: 'yellow500',
  active: 'green500',
  error: 'red500',
};

/** Tone as text or as a status dot. */
export const toneTextColor = (theme: Theme, tone: FlowTone): string =>
  flowToken(theme, TONE_TEXT[tone]);

/**
 * Tone as a connector line.
 *
 * Differs from {@link toneTextColor} in the idle case only: an idle line is
 * structure rather than information, so it drops back to the border grey. At
 * text grey a whole diagram at rest reads as a diagram full of warnings.
 */
export const toneLineColor = (theme: Theme, tone: FlowTone): string =>
  tone === 'idle' ? flowToken(theme, 'wash700') : toneTextColor(theme, tone);

/**
 * Monospace stack for identifiers, paths, ports and URLs.
 *
 * `Sohne Mono` is named first and is not yet bundled — `assets/fonts/Sohne/`
 * ships only buch and kraftig — so today this resolves to the platform mono and
 * will pick the brand face up the moment the woff2 lands.
 */
export const MONO_FONT =
  "'Sohne Mono', ui-monospace, SFMono-Regular, Menlo, monospace";

/**
 * Marching dashes along a connector.
 *
 * -24px is two periods of the 12px repeating gradient the connector paints, so
 * the loop is seamless; any other distance visibly jumps.
 */
export const flowDashes = keyframes`
  to { background-position: -24px 0; }
`;

/** For a status dot on something mid-transition. */
export const flowBlink = keyframes`
  50% { opacity: 0.25; }
`;
